import { COMO_CALCULAMOS_ANCORA_PREFIXO } from '@/components/explica/comoCalculamos';

import { AJUDA_TELAS, TAjudaDuvidaId, TAjudaTela } from './ajudaConteudo';
import { AJUDA_TAREFAS, TAjudaTarefa } from './ajudaTarefas';

/**
 * O endereço da Ajuda: o que está aberto mora na URL, nunca só no estado.
 *
 * As sete abas viraram uma página com atalhos, tarefas e telas, e a Ajuda é o lugar que
 * mais recebe link de fora: o (i) de cada número aponta para um capítulo
 * (`/ajuda#calculo-prazo`) e o "?" de cada tela aponta para a tela dentro do manual. Se o
 * que está aberto não estiver no endereço, nenhum desses links funciona.
 *
 * Os endereços ANTIGOS continuam valendo, por isso o hash ainda é lido: um link já
 * mandado tem de cair no mesmo assunto, e não no topo do manual.
 */

/** As três seções dos atalhos do topo. */
export const AJUDA_SECOES = ['comeceAqui', 'sinaisContas', 'fale'] as const;

export type TAjudaSecao = (typeof AJUDA_SECOES)[number];

export const SECAO_PARAM = 'secao';
export const TAREFA_PARAM = 'tarefa';
export const TELA_PARAM = 'tela';
/** A dúvida a abrir já expandida: é como um resultado da busca entrega a resposta. */
export const DUVIDA_PARAM = 'duvida';

/** Um lugar da Ajuda. Tudo `undefined` é a página inicial do manual. */
export interface IAjudaDestino {
  secao?: TAjudaSecao;
  tarefa?: TAjudaTarefa;
  tela?: TAjudaTela;
  duvida?: TAjudaDuvidaId;
  /** O cartão a rolar depois de a seção abrir (`calculo-prazo`, `sinais-farol`). */
  ancora?: string;
}

/** As âncoras dos grupos de "Cores e sinais", como o componente as escreve. */
export const ANCORA_DE_SINAIS_PREFIXO = 'sinais-';

function isSecao(valor: string): valor is TAjudaSecao {
  return (AJUDA_SECOES as ReadonlyArray<string>).includes(valor);
}

function isTarefa(valor: string): valor is TAjudaTarefa {
  return (AJUDA_TAREFAS as ReadonlyArray<string>).includes(valor);
}

function isTela(valor: string): valor is TAjudaTela {
  return (AJUDA_TELAS as ReadonlyArray<string>).includes(valor);
}

/**
 * O destino que o endereço pede. Um valor que não existe mais (link digitado à mão) abre
 * a página inicial do manual, em vez de uma tela vazia.
 */
export function destinoDaUrl(secao: string, tarefa: string, tela: string, duvida: string): IAjudaDestino {
  const destino: IAjudaDestino = {};
  if (isSecao(secao)) {
    destino.secao = secao;
  }
  if (isTarefa(tarefa)) {
    destino.tarefa = tarefa;
  }
  if (isTela(tela)) {
    destino.tela = tela;
  }
  if (duvida.trim() !== '') {
    destino.duvida = duvida as TAjudaDuvidaId;
  }
  return destino;
}

/**
 * O destino de um hash ANTIGO: as abas de antes, os capítulos do "Como calculamos", os
 * grupos de "Cores e sinais" e o id de uma tela, que é o que o manual sempre usou como
 * âncora. Hash desconhecido não tem destino, e aí a Ajuda abre na página inicial.
 */
export function destinoDoHash(hash: string): IAjudaDestino | null {
  const alvo = hash.replace('#', '').trim();
  if (alvo === '') {
    return null;
  }
  if (alvo.startsWith(COMO_CALCULAMOS_ANCORA_PREFIXO) || alvo.startsWith(ANCORA_DE_SINAIS_PREFIXO)) {
    return { secao: 'sinaisContas', ancora: alvo };
  }
  if (isTela(alvo)) {
    return { tela: alvo };
  }
  // Os nomes das abas de antes, pelo valor que eles tinham em `#`.
  if (alvo === 'comoFunciona') {
    return { secao: 'comeceAqui' };
  }
  if (alvo === 'sinais' || alvo === 'comoCalculamos') {
    return { secao: 'sinaisContas' };
  }
  if (alvo === 'sugestao') {
    return { secao: 'fale' };
  }
  // "As telas" e "Dúvidas frequentes" eram índices, e agora são a própria página inicial.
  return null;
}

/** O mesmo, no navegador, guardado para o render do servidor. */
export function lerDestinoDoHash(): IAjudaDestino | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return destinoDoHash(window.location.hash);
}

/** Um destino escrito como link, para o "?" de uma tela e para o que sai da Ajuda. */
export function ajudaHref(destino: IAjudaDestino): string {
  const params = new URLSearchParams();
  if (destino.secao !== undefined) {
    params.set(SECAO_PARAM, destino.secao);
  }
  if (destino.tarefa !== undefined) {
    params.set(TAREFA_PARAM, destino.tarefa);
  }
  if (destino.tela !== undefined) {
    params.set(TELA_PARAM, destino.tela);
  }
  if (destino.duvida !== undefined) {
    params.set(DUVIDA_PARAM, destino.duvida);
  }
  const busca = params.toString();
  const ancora = destino.ancora === undefined ? '' : `#${destino.ancora}`;
  return `/ajuda${busca === '' ? '' : `?${busca}`}${ancora}`;
}
